import { AuthFetchWithResponse } from 'src/Types/api'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { ApiResult } from '../../api'
import { StatusCodes } from '../../http-status-codes'
import { handleFetchError } from 'src/Libs/api/utils'
import { ConfigSyncStatusDto, ConfigSyncTriggerDto } from './dto/config-sync.dto'
import { ConfigSyncDeviceStatusModel } from '../models/config-sync.model'
import configSyncStatusDtoToModelTransformer from './transformer/config-sync-to-model.transformer'

/**
 * Fetches the last known config-sync state per device (DB snapshot only, no live device roundtrip).
 * GET /installations/{iid}/config/sync
 */
export const fetchConfigSyncStatus = async (
  authFetch: AuthFetchWithResponse,
  installationId: number | undefined
): Promise<ApiResult<ConfigSyncDeviceStatusModel[], AuthFetchError | Error>> => {
  try {
    if (!installationId) {
      return handleFetchError(new Error('installationId missing'))
    }

    const response = await authFetch<ConfigSyncStatusDto>({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/config/sync`,
      method: 'GET',
    })

    // 204: no sync has run for this installation yet, so there is no per-device state.
    if (response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<ConfigSyncDeviceStatusModel[], AuthFetchError | Error>([])
    }

    if (response.status === StatusCodes.OK) {
      return new ApiResult<ConfigSyncDeviceStatusModel[], AuthFetchError | Error>(configSyncStatusDtoToModelTransformer(response.data))
    }

    return new ApiResult<ConfigSyncDeviceStatusModel[], AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<ConfigSyncDeviceStatusModel[]>(error)
  }
}

/**
 * Triggers a synchronous config sync for all relevant devices of the installation: the backend
 * queries every device live via RSCP, (re-)generates the config and writes it back before
 * responding, so this can take a while depending on device count/reachability.
 * POST /installations/{iid}/config/sync
 */
export const triggerConfigSync = async (
  authFetch: AuthFetchWithResponse,
  installationId: number | undefined
): Promise<ApiResult<ConfigSyncTriggerDto, AuthFetchError | Error>> => {
  try {
    if (!installationId) {
      return handleFetchError(new Error('installationId missing'))
    }

    const response = await authFetch<ConfigSyncTriggerDto>({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/config/sync`,
      method: 'POST',
    })

    // Non-2xx responses (including the backend's `errno: 404`-but-HTTP-400 edge case) are thrown by
    // axios and land in the catch block below — no need to special-case them here.
    if (response.status === StatusCodes.OK) {
      return new ApiResult<ConfigSyncTriggerDto, AuthFetchError | Error>(response.data)
    }

    return new ApiResult<ConfigSyncTriggerDto, AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<ConfigSyncTriggerDto>(error)
  }
}
